/**
 * Item Template Toggle Widget
 *
 * Switch component for enabling/disabling variant template mode.
 * Renders in its own card section, matching the design of other widgets.
 * Includes VariantBuilder when template mode is enabled.
 *
 * Safety guards (Epic 02):
 * - Toggle-OFF confirmation when existing variants exist
 * - Passes existingVariantCount to VariantBuilder for attribute removal warnings
 */

import { Suspense, lazy, useState, useCallback } from 'react';
import { cn } from "@/lib/utils";
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Archive, Package } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { frappeMethod } from '@/lib/erp/utils';
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from '../types';
import { useOptionalItemVariantDraft } from '../../../../contexts/ItemVariantDraftContext';
import { createDefaultVariantDraft } from '@/lib/erp/variants';
import { useTemplateVariantsSummary } from '../../../../hooks/useTemplateVariantsSummary';
import { useLanguage } from '@/i18n/useLanguage';

const VARIANT_LABELS: Record<string, Record<string, string>> = {
  variants: { en: "Variants", th: "ตัวเลือกสินค้า" },
  enableVariantMode: { en: "Enable variant template mode", th: "เปิดใช้งานโหมดตัวเลือกสินค้า" },
  disableVariantMode: { en: "Disable variant mode?", th: "ปิดโหมดตัวเลือกสินค้า?" },
  archiveAll: { en: "Archive all variants", th: "เก็บถาวรตัวเลือกทั้งหมด" },
  keepStandalone: { en: "Keep as standalone items", th: "เก็บไว้เป็นสินค้าแยก" },
};

// Lazy load VariantBuilder (not in initial bundle)
const VariantBuilder = lazy(() => import('../../../../variants/VariantBuilder'));

export function ItemTemplateToggleWidget({
  section,
  formData,
  onFieldChange,
  disabled,
}: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const vl = (key: string) => VARIANT_LABELS[key]?.[language] ?? VARIANT_LABELS[key]?.en ?? key;
  const isTemplateMode = formData?.has_variants === 1 || formData?.has_variants === true;
  const variantDraftContext = useOptionalItemVariantDraft();
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const isBundleMode = formData?.__bundle_mode === 1;

  // Determine if this is an existing item (not new)
  const isNew = !formData?.name || String(formData.name).startsWith('new-');

  // Fetch existing variant count for safety guards (only for existing template items)
  const { data: summaryData } = useTemplateVariantsSummary(
    formData?.item_code ?? '',
    isTemplateMode && !isNew,
  );
  const existingVariantCount = summaryData?.total_variants ?? 0;

  const queryClient = useQueryClient();

  // Bulk archive all variants when user picks "Archive all"
  const bulkArchiveMutation = useMutation({
    mutationFn: async () => {
      // Fetch ALL variant names (not capped at 200 like summary)
      const variants = await frappeMethod<Array<{ name: string }>>(
        'frappe.client.get_list',
        {
          doctype: 'Item',
          filters: JSON.stringify({ variant_of: formData?.item_code, disabled: 0 }),
          fields: JSON.stringify(['name']),
          limit_page_length: 0,
        },
      );
      if (!variants || variants.length === 0) return;
      const docs = variants.map((v) => ({
        doctype: 'Item',
        docname: v.name,
        disabled: 1,
      }));
      await frappeMethod('frappe.client.bulk_update', {
        docs: JSON.stringify(docs),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['template-variants-summary', formData?.item_code] });
      queryClient.invalidateQueries({ queryKey: ['variant-archived-count', formData?.item_code] });
      queryClient.invalidateQueries({ queryKey: ['archived-count'] });
      toast.success(`${existingVariantCount} variant${existingVariantCount !== 1 ? 's' : ''} archived.`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to archive variants');
    },
  });

  const executeDisable = () => {
    onFieldChange('has_variants', 0);
    onFieldChange('variant_based_on', undefined);
    onFieldChange('attributes', []);
    if (variantDraftContext) {
      variantDraftContext.setVariantDraft(createDefaultVariantDraft());
    }
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      // Enable template mode
      onFieldChange('has_variants', 1);
      onFieldChange('variant_based_on', 'Item Attribute');
      // Reset variant draft when enabling
      if (variantDraftContext) {
        variantDraftContext.setVariantDraft(createDefaultVariantDraft());
      }
    } else {
      // Disable template mode — show confirmation if existing variants exist
      // Fail open: if summaryData hasn't loaded yet, skip dialog
      if (existingVariantCount > 0) {
        setShowDisableConfirm(true);
      } else {
        executeDisable();
      }
    }
  };

  const handleArchiveAllAndDisable = useCallback(() => {
    bulkArchiveMutation.mutate(undefined, {
      onSuccess: () => {
        executeDisable();
        setShowDisableConfirm(false);
      },
    });
  }, [bulkArchiveMutation, executeDisable]);

  const handleKeepStandaloneAndDisable = useCallback(() => {
    executeDisable();
    setShowDisableConfirm(false);
  }, [executeDisable]);

  // Mutual exclusivity: hide if bundle mode is enabled (bundles can't be templates)
  // IMPORTANT: This guard must be AFTER all hooks to satisfy React Rules of Hooks
  if (isBundleMode) return null;

  return (
    <Card className={cn(CARD, "mb-6 cursor-pointer hover:shadow-md transition-shadow")}>
      <CardContent className="p-8 sm:p-10">
        <div className="space-y-6">
          {/* Switch Row */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="template-mode-toggle" className="text-base font-medium cursor-pointer">
                {vl("variants")}
              </Label>
              {section.description && (
                <p className="text-sm text-muted-foreground">
                  {section.description}
                </p>
              )}
            </div>
            <Switch
              id="template-mode-toggle"
              checked={isTemplateMode}
              onCheckedChange={handleToggle}
              disabled={disabled}
              aria-label={vl("enableVariantMode")}
            />
          </div>

          {/* Variant Builder - shown when template mode is enabled */}
          {isTemplateMode && variantDraftContext && (
            <div className="pt-6 border-t border-border/20">
              <Suspense fallback={<div className="text-muted-foreground text-sm p-4">Loading variant builder...</div>}>
                <VariantBuilder
                  variant="embedded"
                  draft={variantDraftContext.variantDraft}
                  onDraftChange={variantDraftContext.setVariantDraft}
                  templateCode={formData.item_code}
                  selectedVariantIds={variantDraftContext.selectedVariantIds}
                  onSelectionChange={variantDraftContext.setSelectedVariantIds}
                  existingVariantCount={existingVariantCount}
                />
              </Suspense>
            </div>
          )}
        </div>
      </CardContent>

      {/* Toggle-OFF guard: 3-option dialog */}
      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{vl("disableVariantMode")}</AlertDialogTitle>
            <AlertDialogDescription>
              This template has {existingVariantCount} variant{existingVariantCount !== 1 ? 's' : ''}.
              Choose what to do with them:
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            {/* Option 1: Archive all variants */}
            <button
              type="button"
              onClick={handleArchiveAllAndDisable}
              disabled={bulkArchiveMutation.isPending}
              className="w-full rounded-lg border p-4 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <Archive className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{vl("archiveAll")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Hide {existingVariantCount} variant{existingVariantCount !== 1 ? 's' : ''} from lists. Restore anytime.
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Keep as standalone items */}
            <button
              type="button"
              onClick={handleKeepStandaloneAndDisable}
              disabled={bulkArchiveMutation.isPending}
              className="w-full rounded-lg border p-4 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <Package className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{vl("keepStandalone")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Variants stay active but this product won't be a template.
                  </p>
                </div>
              </div>
            </button>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkArchiveMutation.isPending}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
